import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import useSWR from "swr";
import { GoBack, PrimaryButton } from "../components/Buttons";
import { CollapsibleSection } from "../components/Cards";
import { PhoneInput, SelectInput, TextInput, NumberInput, CheckboxInput, DateInput, PasswordInput, ToggleInput, AvatarPictureUpload } from "../components/Inputs";
import { ModalWrapper } from "../components/Modals";
import { PageHeader } from "../components/Layouts";
import { TabView } from "../components/Tabs";
import { useLoaderStore, useUserStore } from "../context";
import { useAlert, useApi } from "../hooks";
import constants from "../utils/constants";
import { convertToIdNameList, isValidEmail, convertToLabelValueList, convertToLabelValue, resolveSystemTenantId, switchSortDirection, isDev, classNames, tryParseValidationErrorMessage, isInvalidID } from "../utils/helpers";
import { UserCircleIcon, ChatBubbleBottomCenterTextIcon, QueueListIcon, BuildingStorefrontIcon, KeyIcon } from "@heroicons/react/24/outline";
import { ActiveFilter, PrimaryTable } from "../components/Tables";
import dateHelpers from "../utils/dateHelpers";
import { InfoTooltip } from "../components/Displays";
import SystemTenantApiKeys from "./SystemTenantApiKeys";
import APIActivities from "./APIActivities";
import Notifications from "./Notifications";

const EMPTY_FILTERS = {
  activeOnly: false,
};

export default function SystemTenant() {
  let { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const alert = useAlert();
  const isCreatingNew = isInvalidID(id);
  console.log("isCreatingNew", isCreatingNew)
  const { fetch, post, postFormData } = useApi();
  const queryTab = searchParams.get('tab');
  const { PROFILE, ACTIVITY, NOTIFICATIONS, API_KEYS } = constants.SYS_TENANT_TABS;
  const { setShowLoader } = useLoaderStore();
  const { currentUser, activeSystemTenant } = useUserStore();

  //SystemTenant Variables
  const [systemTenant, setSystemTenant] = useState({ id });
  const [showFullDetails, setShowFullDetails] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [showResetConfirmation, setShowResetConfirmation] = useState(false);
  const [activeTab, setActiveTab] = useState(queryTab || PROFILE);
  const [freeDefaultProductLicenseIdData, setFreeDefaultProductLicenseIdData] = useState([]);
  
  //Activity Variables
  const [isLoading, setIsLoading] = useState(false);
  const [activeFilters, setActiveFilters] = useState({
    ...EMPTY_FILTERS,
    activeOnly: true,
  });
  const [notificationCurrentPage, setNotificationCurrentPage] = useState(0);
  const [notificationSortDirection, setNotificationSortDirection] = useState(
    constants.SORT_DIRECTIONS.DESCENDING
  );
  const [notificationSortField, setNotificationSortField] = useState("sentAt");
  const IS_DEV = isDev();

  useEffect(() => {
    if(isDirty) {
      setShowResetConfirmation(true);
    }
    else {
      getSystemTenant();
    }
  }, [showFullDetails]);

  useEffect(() => {
    if (isCreatingNew) return;
    fetch("admin/getProductsSimple", { sortField: "Name", systemTenantId: id, priceEqual: 0 })
    .then((res) => {
        setFreeDefaultProductLicenseIdData(res);
    });
  }, [])

  useEffect(() => {
    if (activeTab === ACTIVITY || activeTab === NOTIFICATIONS) {
      setNotificationCurrentPage(1);
    }
  }, [activeTab])

  function getSystemTenant() {
    if (isCreatingNew) return;
    setShowLoader(true);
    fetch(`admin/systemTenantInfo/${id}/${showFullDetails}`)
      .then((res) => {
        setSystemTenant(res);
        setIsDirty(false);
      })
      .catch(() => {
        alert("Server error", "Could not retrieve systemTenant", "error");
      })
      .finally(() => setShowLoader(false));
  }

  const handleChange = (property, value) => {
    setSystemTenant({
      ...systemTenant,
      [property]: value,
    });
    if(!isDirty){
      setIsDirty(true);
    }
  };

  const handleCredentialChange = (property, value) => {
    let copy = {
      ...systemTenant
    };
    if(!copy.credentials) copy.credentials = {};
    copy.credentials[property] = value;
    setSystemTenant(copy);
    if(!isDirty){
      setIsDirty(true);
    }
  };

  const handleSave = (reloadSystemTenant = false) => {
    if (!isValid() || (!activeSystemTenant && isCreatingNew)) return;

    setShowLoader(true);
    let payload = {
      systemTenant: systemTenant
    };
    if (isCreatingNew) {
      payload.systemTenant.accountId = activeSystemTenant.accountId;
    }
    if (systemTenant.image) {
      payload.file = systemTenant.image.fileContents
    }
    let saveCredentials = showFullDetails && systemTenant.credentials !== null;
    postFormData(`admin/saveSystemTenant/${saveCredentials}`, payload)
      .then((res) => {
        alert("Success", "Saved systemTenant", "success");
        if (reloadSystemTenant) {
          //set id to the newly saved system tenant id
          if(isCreatingNew && res.id) id = res.id;
          getSystemTenant();
        }
        else {
          navigate("/systemTenants");
        }
      })
      .catch((err) => {
        // console.error("ERROR", err)
        alert(
          "Unable to save systemTenant",
          tryParseValidationErrorMessage(err) || "Please try again",
          "error"
        );
        console.error(err);
      })
      .finally(() => {
        setShowLoader(false);
      });
    // post(`admin/saveSystemTenant/${saveCredentials}`, payload)
    //   .then((res) => {
    //     alert("Success", "Saved systemTenant", "success");
    //     if (reloadSystemTenant) {
    //       //set id to the newly saved system tenant id
    //       if(isCreatingNew && res.id) id = res.id;
    //       getSystemTenant();
    //     }
    //     else {
    //       navigate("/systemTenants");
    //     }
    //   })
    //   .catch((err) => {
    //     alert(
    //       "Unable to save systemTenant",
    //       err.data.message || "Please try again",
    //       "error"
    //     );
    //     console.error(err);
    //   })
    //   .finally(() => {
    //     setShowLoader(false);
    //   });
  };

  const isValid = () => {
    if (!systemTenant.name || !systemTenant.name.trim()) {
      alert("Form invalid", "Name must have a value", "warning");
      return false;
    }
    if (!systemTenant.processRenewalMinutesThreshold) {
      alert("Form invalid", "Process Renewal Hours Threshold must have a value", "warning");
      return false;
    }
    if (!systemTenant.adminEmail || !systemTenant.adminEmail.trim()) {
      alert("Form invalid", "Admin Email must have a value", "warning");
      return false;
    }
    if (!systemTenant.adminMobilePhone || !systemTenant.adminMobilePhone.trim()) {
      alert("Form invalid", "Admin Mobile Phone must have a value", "warning");
      return false;
    }
    if (!systemTenant.getTenantUsersUri || !systemTenant.getTenantUsersUri.trim()) {
      alert("Form invalid", "Get Tenant Users Uri must have a value", "warning");
      return false;
    }
    // if (!systemTenant.tenantUserCommunicationSentUri || !systemTenant.tenantUserCommunicationSentUri.trim()) {
    //   alert("Form invalid", "Tenant User Communication Sent Uri must have a value", "warning");
    //   return false;
    // }
    //ALL CREDENTIALS ARE OPTIONAL
    // if(showFullDetails) {
    //   if (!systemTenant.credentials?.stripeApiKey || !systemTenant.credentials?.stripeApiKey.trim()) {
    //     alert("Form invalid", "Stripe Api Key must have a value", "warning");
    //     return false;
    //   }
    //   if (!systemTenant.credentials?.smtpMailFromAddress || !systemTenant.credentials?.smtpMailFromAddress.trim()) {
    //     alert("Form invalid", "SMTP Mail From Address must have a value", "warning");
    //     return false;
    //   }
    //   if (!systemTenant.credentials?.smtpMailFromName || !systemTenant.credentials?.smtpMailFromName.trim()) {
    //     alert("Form invalid", "SMTP Mail From Name must have a value", "warning");
    //     return false;
    //   }
    //   if (!systemTenant.credentials?.smtpServer || !systemTenant.credentials?.smtpServer.trim()) {
    //     alert("Form invalid", "SMTP Server must have a value", "warning");
    //     return false;
    //   }
    //   if (!systemTenant.credentials?.smtpPort) {
    //     alert("Form invalid", "SMTP Port must have a value", "warning");
    //     return false;
    //   }
    //   if (typeof systemTenant.credentials?.smtpServerRequiresAuthentication !== 'boolean') {
    //     alert("Form invalid", "SMTP Server Requires Authentication must have a value", "warning");
    //     return false;
    //   }
    //   if (typeof systemTenant.credentials?.smtpServerRequiresSsl !== 'boolean') {
    //     alert("Form invalid", "SMTP Server Requires Ssl must have a value", "warning");
    //     return false;
    //   }
    //   if (!systemTenant.credentials?.smtpServerPassword || !systemTenant.credentials?.smtpServerPassword.trim()) {
    //     alert("Form invalid", "SMTP Server Password must have a value", "warning");
    //     return false;
    //   }
    // }

    return true;
  };

  const handleSort = (field) => {
    if (notificationSortField === field) {
      setNotificationSortDirection(switchSortDirection(notificationSortDirection));
    } else {
      setNotificationSortField(field);
    }
  };

  const getTabs = () => {
    let tabs = [
      {name: PROFILE, icon: BuildingStorefrontIcon},
    ];
    if (!isCreatingNew) {
      tabs.push({name: ACTIVITY, icon: QueueListIcon});
      tabs.push({name: NOTIFICATIONS, icon: ChatBubbleBottomCenterTextIcon});
      tabs.push({name: API_KEYS, icon: KeyIcon});
    }
    return tabs;
  }

  return (
    <div>
      {/* Header */}
      <PageHeader
        title={
          isCreatingNew || !systemTenant ? "Create new System Tenant" : `Manage System Tenant ${IS_DEV ? systemTenant.id : ""}`
        }
      />
      <GoBack display="Manage all System Tenants" navigateTo="/systemTenants" />
      <div className="flex flex-col lg:flex-row justify-between">
        <TabView 
          tabs={getTabs()}
          setActiveTab={setActiveTab}
          activeTab={activeTab}
        />
        {activeTab == PROFILE && (
        <div className="flex flex-row">
          <ToggleInput
            // label={"Show credentials"}
            enabled={showFullDetails}
            setEnabled={setShowFullDetails}
          />
          <span className="ml-3">Show credentials</span>
        </div>)}
      </div>
      {activeTab === PROFILE && (
        <div className="bg-gray-100 shadow-md rounded-md shadow-primaryColor/25">
          {/* Form */}
          <CollapsibleSection title="Info">
            <div className="p-4 flex flex-row justify-center">
              <div className="max-w-xl">
                <AvatarPictureUpload
                  defaultImageUrl={systemTenant?.signedLogoUri}
                  imageUrlParam={systemTenant?.image}
                  imageAlt={"System tenant logo"}
                  onChange={(e) => handleChange("image", e)}
                  onRemoveImage={() => {
                    setSystemTenant({ ...systemTenant, image: undefined, logoUri: undefined })
                  }}
                />
              </div>
            </div>          
            <div className="p-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-4">
              <div>
                <TextInput
                  label="Name"
                  value={systemTenant?.name ?? ""}
                  onChange={(e) => handleChange("name", e.target.value)}
                  required
                  maxLength={200}
                />
              </div>
              <div>
                <SelectInput
                  idParam={constants.EXP_IDS.FreeDefault}
                  label="Free Default Product License Id"
                  options={convertToLabelValueList(freeDefaultProductLicenseIdData)}
                  onClear={() => handleChange("freeDefaultProductLicenseId", undefined)}
                  value={convertToLabelValue(freeDefaultProductLicenseIdData?.find(x => x.id === systemTenant?.freeDefaultProductLicenseId))}
                  onChange={(freeDefaultProductLicenseId) => handleChange("freeDefaultProductLicenseId", freeDefaultProductLicenseId.value)}
                  isSearchable
                />
                <InfoTooltip id={`#${constants.EXP_IDS.FreeDefault}`} place="top">
                  {constants.EXPLANATIONS.FreeDefault}
                </InfoTooltip>
              </div>
              <div>
                <NumberInput
                  id={constants.EXP_IDS.Grace}
                  label="Delinquent Grace Period Minutes"
                  value={systemTenant?.delinquentGracePeriodMinutes ?? ""}
                  onChange={(e) => handleChange("delinquentGracePeriodMinutes", e.target.valueAsNumber)}
                />
                <InfoTooltip id={`#${constants.EXP_IDS.Grace}`}>
                  {constants.EXPLANATIONS.Grace}
                </InfoTooltip>
              </div>
              <div>
                <NumberInput
                  id={constants.EXP_IDS.WarnRenewal}
                  label="Warn Renewal Minutes Threshold"
                  value={systemTenant?.warnRenewalMinutesThreshold ?? ""}
                  onChange={(e) => handleChange("warnRenewalMinutesThreshold", e.target.valueAsNumber)}
                />
                <InfoTooltip id={`#${constants.EXP_IDS.WarnRenewal}`}>
                  {constants.EXPLANATIONS.WarnRenewal}
                </InfoTooltip>
              </div>
              <div>
                <NumberInput
                  id={constants.EXP_IDS.WarnCancel}
                  label="Warn Cancellation Minutes Threshold"
                  value={systemTenant?.warnCancellationMinutesThreshold ?? ""}
                  onChange={(e) => handleChange("warnCancellationMinutesThreshold", e.target.valueAsNumber)}
                />
                <InfoTooltip id={`#${constants.EXP_IDS.WarnCancel}`}>
                  {constants.EXPLANATIONS.WarnCancel}
                </InfoTooltip>
              </div>
              <div>
                <NumberInput
                  id={constants.EXP_IDS.Renew}
                  label="Process Renewal Minutes Threshold"
                  value={systemTenant?.processRenewalMinutesThreshold ?? ""}
                  onChange={(e) => handleChange("processRenewalMinutesThreshold", e.target.valueAsNumber)}
                  required
                />
                <InfoTooltip id={`#${constants.EXP_IDS.Renew}`}>
                  {constants.EXPLANATIONS.Renew}
                </InfoTooltip>
              </div>
              <div>
                <TextInput
                  id={constants.EXP_IDS.EmailNotification}
                  label="Admin Email"
                  value={systemTenant?.adminEmail ?? ""}
                  onChange={(e) => handleChange("adminEmail", e.target.value)}
                  required
                  maxLength={150}
                />
                <InfoTooltip id={`#${constants.EXP_IDS.EmailNotification}`}>
                  {constants.EXPLANATIONS.EmailNotification}
                </InfoTooltip>
              </div>
              <div>
                <TextInput
                  id={constants.EXP_IDS.MobileNotification}
                  label="Admin Mobile Phone"
                  value={systemTenant?.adminMobilePhone ?? ""}
                  onChange={(e) => handleChange("adminMobilePhone", e.target.value)}
                  required
                  maxLength={25}
                />
                <InfoTooltip id={`#${constants.EXP_IDS.MobileNotification}`}>
                  {constants.EXPLANATIONS.MobileNotification}
                </InfoTooltip>
              </div>
              <div>
                <TextInput
                  label="Logo Uri"
                  value={systemTenant?.logoUri ?? ""}
                  onChange={(e) => handleChange("logoUri", e.target.value)}
                  maxLength={300}
                />
              </div>
              <div>
                <TextInput
                  id={constants.EXP_IDS.GetTenantUsers}
                  label="Get Tenant Users Uri"
                  value={systemTenant?.getTenantUsersUri ?? ""}
                  onChange={(e) => handleChange("getTenantUsersUri", e.target.value)}
                  required
                  maxLength={300}
                />
                <InfoTooltip id={`#${constants.EXP_IDS.GetTenantUsers}`}>
                  {constants.EXPLANATIONS.GetTenantUsers}
                </InfoTooltip>
              </div>
              <div>
                <TextInput
                  id={constants.EXP_IDS.CommunicationSent}
                  label="Tenant User Communication Sent Uri"
                  value={systemTenant?.tenantUserCommunicationSentUri ?? ""}
                  onChange={(e) => handleChange("tenantUserCommunicationSentUri", e.target.value)}
                  maxLength={300}
                />
                <InfoTooltip id={`#${constants.EXP_IDS.CommunicationSent}`}>
                  {constants.EXPLANATIONS.CommunicationSent}
                </InfoTooltip>
              </div>
              <div>
                <TextInput
                  label="Tenant Subscription Changed Uri"
                  value={systemTenant?.tenantSubscriptionChangedUri ?? ""}
                  onChange={(e) => handleChange("tenantSubscriptionChangedUri", e.target.value)}
                  maxLength={300}
                />
              </div>
              <div>
                <TextInput
                  id={constants.EXP_IDS.Signature}
                  label="Client Communication Signature"
                  value={systemTenant?.clientCommunicationSignature ?? ""}
                  onChange={(e) => handleChange("clientCommunicationSignature", e.target.value)}
                  maxLength={300}
                />
                <InfoTooltip id={`#${constants.EXP_IDS.Signature}`}>
                  {constants.EXPLANATIONS.Signature}
                </InfoTooltip>
              </div>
            </div>
          </CollapsibleSection>
          {showFullDetails && (
            <CollapsibleSection title="Credentials" startOpen={showFullDetails}>
              <div className="flex flex-col gap-y-4">
                <div>
                  <h3 className="py-2 px-4 font-semibold">Stripe</h3>
                  <div className="p-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-4 border-b-2">
                    <div>
                      <PasswordInput
                        label="Stripe Api Key"
                        value={systemTenant?.credentials?.stripeApiKey ?? ""}
                        onChange={(e) => handleCredentialChange("stripeApiKey", e.target.value)}
                        required
                        maxLength={150}
                      />
                    </div>
                  </div>
                </div>
                <div>
                  {/*
                    SPR-016 T-004. Optional. When it is blank Margot sends only the legacy margot-okapi-hash, exactly
                    as it always has. When it is set, outbound webhooks also carry
                    X-Margot-Signature: sha256=<hex of HMAC-SHA256 over the raw body> keyed with THIS value, which the
                    receiver must have configured identically. Masked like every other credential here, and never
                    logged.
                  */}
                  <h3 className="py-2 px-4 font-semibold">Outbound Webhooks</h3>
                  <div className="p-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-4 border-b-2">
                    <div>
                      <PasswordInput
                        label="Webhook Signing Secret"
                        value={systemTenant?.credentials?.webhookSigningSecret ?? ""}
                        onChange={(e) => handleCredentialChange("webhookSigningSecret", e.target.value)}
                        maxLength={200}
                      />
                    </div>
                  </div>
                </div>
                <div>
                  <h3 className="py-2 px-4 font-semibold">Twilio/SMS</h3>
                  <div className="p-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-4 border-b-2">
                  <div>
                    <PasswordInput
                      label="Twilio Account Sid"
                      value={systemTenant?.credentials?.twilioAccountSid ?? ""}
                      onChange={(e) => handleCredentialChange("twilioAccountSid", e.target.value)}
                      maxLength={100}
                    />
                  </div>
                  <div>
                    <PasswordInput
                      label="Twilio Auth Token"
                      value={systemTenant?.credentials?.twilioAuthToken ?? ""}
                      onChange={(e) => handleCredentialChange("twilioAuthToken", e.target.value)}
                      maxLength={100}
                    />
                  </div>
                  <div>
                    <PasswordInput
                      label="Twilio Send From Number"
                      value={systemTenant?.credentials?.twilioSendFromNumber ?? ""}
                      onChange={(e) => handleCredentialChange("twilioSendFromNumber", e.target.value)}
                      maxLength={25}
                    />
                  </div>
                  <div>
                    <PasswordInput
                      label="Twilio Send From Message Sid"
                      value={systemTenant?.credentials?.twilioSendFromMessageSid ?? ""}
                      onChange={(e) => handleCredentialChange("twilioSendFromMessageSid", e.target.value)}
                      maxLength={100}
                    />
                  </div>
                  </div>
                </div>
                <div>
                  <h3 className="py-2 px-4 font-semibold">Email</h3>
                  <div className="p-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-4 border-b-2">
                  <div>
                    <PasswordInput
                      label="SMTP Mail From Address"
                      value={systemTenant?.credentials?.smtpMailFromAddress ?? ""}
                      onChange={(e) => handleCredentialChange("smtpMailFromAddress", e.target.value)}
                      required
                      maxLength={150}
                    />
                  </div>
                  <div>
                    <PasswordInput
                      label="SMTP Mail From Name"
                      value={systemTenant?.credentials?.smtpMailFromName ?? ""}
                      onChange={(e) => handleCredentialChange("smtpMailFromName", e.target.value)}
                      required
                      maxLength={100}
                    />
                  </div>
                  <div>
                    <PasswordInput
                      label="SMTP Server"
                      value={systemTenant?.credentials?.smtpServer ?? ""}
                      onChange={(e) => handleCredentialChange("smtpServer", e.target.value)}
                      required
                      maxLength={150}
                    />
                  </div>
                  <div>
                    <PasswordInput
                      label="SMTP Port"
                      value={systemTenant?.credentials?.smtpPort ?? ""}
                      onChange={(e) =>{
                        let value = Number.parseInt(e.target.value, 10);
                        if(Number.isNaN(value) && e.target.value) return;
                        if(Number.isNaN(value) && !e.target.value) value = null;
                        handleCredentialChange("smtpPort", value);
                        }}
                      required
                    />
                  </div>
                  <div>
                    <PasswordInput
                      label="SMTP Server Username"
                      value={systemTenant?.credentials?.smtpServerUsername ?? ""}
                      onChange={(e) => handleCredentialChange("smtpServerUsername", e.target.value)}
                      maxLength={100}
                    />
                  </div>
                  <div>
                    <PasswordInput
                      label="SMTP Server Password"
                      value={systemTenant?.credentials?.smtpServerPassword ?? ""}
                      onChange={(e) => handleCredentialChange("smtpServerPassword", e.target.value)}
                      required
                      maxLength={100}
                    />
                  </div>
                  <div>
                    <CheckboxInput
                      label="SMTP Server Requires Authentication?"
                      checked={systemTenant?.credentials?.smtpServerRequiresAuthentication}
                      onChange={(e) => handleCredentialChange("smtpServerRequiresAuthentication", !systemTenant?.credentials?.smtpServerRequiresAuthentication)}
                    />
                  </div>
                  <div>
                    <CheckboxInput
                      label="SMTP Server Requires Ssl?"
                      checked={systemTenant?.credentials?.smtpServerRequiresSsl}
                      onChange={(e) => handleCredentialChange("smtpServerRequiresSsl", !systemTenant?.credentials?.smtpServerRequiresSsl)}
                    />
                  </div>
                  </div>
                </div>
              </div>
            </CollapsibleSection>
          )}
          {/* Submit button */}
          <div className={classNames("mt-12", constants.SAVE_BUTTON_CONTAINER_CLASS.default)}>
            <PrimaryButton
              text={isCreatingNew ? "Create" : "Save"}
              onClick={handleSave}
            />
          </div>
          <div>
            <ModalWrapper open={showResetConfirmation} setOpen={setShowResetConfirmation}>
              <div className="w-[85vw] lg:w-[35vw]">
                <div className="pb-4 text-center flex items-center flex-col">
                  {/* <Icon className="w-5 text-gray-500" /> */}
                  Unsaved changes
                </div>
                <div className="pb-8 mx-6 text-center text-sm text-gray-500">
                  {`You have unsaved changes. Would you like to save them before ${showFullDetails ? "loading" : "hiding"} credentials.`}
                </div>
                <div className="mt-3 flex flex-col lg:flex-row justify-between gap-2">
                  <PrimaryButton
                    text={"Yes"}
                    onClick={() => {
                      setShowResetConfirmation(false);
                      handleSave(true);
                    }}
                  />
                  <PrimaryButton
                    text={"No"}
                    onClick={() => {
                      setShowResetConfirmation(false);
                      getSystemTenant();
                    }}
                  />
                </div>
              </div>
            </ModalWrapper>
          </div>
        </div>
      )}
      <div className={activeTab === ACTIVITY ? "" : "hidden"}>
        <APIActivities systemTenantId={systemTenant?.id} />
      </div>
      <div className={activeTab === NOTIFICATIONS ? "" : "hidden"}>
        <Notifications systemTenantId={systemTenant?.id} />
      </div>
      <div className={activeTab === API_KEYS ? "" : "hidden"}>
        <SystemTenantApiKeys systemTenantIdProp={systemTenant?.id} />
      </div>
    </div>
  );
}
